import { useState, useEffect } from 'react';
// `Facebook` was dropped from lucide-react (brand icons removed for trademark
// reasons) when this branch upgraded to 1.21.0; `Users` stands in for it.
import { MessageSquare, Filter, ChevronLeft, ChevronRight, Star, Users, Share2, BarChart3 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { adminApi } from '@/services/adminApi';
import LoadingSpinner from '@/components/common/LoadingSpinner';

interface Survey {
  surveyId: string;
  respondentType: 'student' | 'parent';
  respondentName: string;
  respondentEmail: string;
  studentName: string;
  ratings: Record<string, number>;
  freeText: string | null;
  willingToReviewFacebook: boolean | null;
  willingToShare: boolean | null;
  completedAt: string | null;
}

interface SurveyStats {
  totalCompleted: number;
  studentCount: number;
  parentCount: number;
  studentAverages: Record<string, number>;
  parentAverages: Record<string, number>;
  facebookWillingCount: number;
  shareWillingCount: number;
}

const STUDENT_LABELS: Record<string, string> = {
  preparedness: 'Prepared for real life',
  enjoyment: 'Enjoyed the curriculum',
  confidence: 'Confident in financial future',
  recommend: 'Would recommend to a friend',
};

const PARENT_LABELS: Record<string, string> = {
  gladFound: 'Glad they found BVA',
  engagement: 'Teen was engaged',
  preparedness: 'Prepared teen for decisions',
  recommend: 'Would recommend to families',
};

function averageRating(ratings: Record<string, number>): number {
  const values = Object.values(ratings);
  if (values.length === 0) return 0;
  return Math.round((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
}

export function SurveysPage() {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [stats, setStats] = useState<SurveyStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const limit = 20;

  // Filters
  const [respondentType, setRespondentType] = useState('');
  const [facebookFilter, setFacebookFilter] = useState('');
  const [shareFilter, setShareFilter] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    loadSurveys();
  }, [page, respondentType, facebookFilter, shareFilter]);

  const loadStats = async () => {
    try {
      const data = await adminApi.getSurveyStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to load survey stats:', err);
    }
  };

  const loadSurveys = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getSurveys({
        respondentType: respondentType || undefined,
        willingToReviewFacebook: facebookFilter || undefined,
        willingToShare: shareFilter || undefined,
        page,
        limit,
      });
      setSurveys(data.surveys);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    setPage(1);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Survey Feedback</h1>
        <p className="text-muted-foreground mt-1">
          View course completion survey responses and aggregate ratings
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Card className="py-4">
            <CardContent>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Total Responses</p>
                  <p className="text-3xl font-bold text-foreground mt-1">{stats.totalCompleted}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {stats.studentCount} students, {stats.parentCount} parents
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/20">
                  <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="py-4">
            <CardContent>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Student Avg Rating</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {Object.keys(stats.studentAverages).length > 0
                      ? (Object.values(stats.studentAverages).reduce((a, b) => a + b, 0) / Object.values(stats.studentAverages).length).toFixed(1)
                      : '-'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">out of 5.0</p>
                </div>
                <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/20">
                  <Star className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="py-4">
            <CardContent>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Parent Avg Rating</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {Object.keys(stats.parentAverages).length > 0
                      ? (Object.values(stats.parentAverages).reduce((a, b) => a + b, 0) / Object.values(stats.parentAverages).length).toFixed(1)
                      : '-'}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">out of 5.0</p>
                </div>
                <div className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/20">
                  <Star className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="py-4">
            <CardContent>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Willing to Promote</p>
                  <p className="text-3xl font-bold text-foreground mt-1">
                    {stats.facebookWillingCount + stats.shareWillingCount}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {stats.facebookWillingCount} Facebook, {stats.shareWillingCount} share
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-900/20">
                  <Share2 className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rating Breakdown */}
      {stats && (Object.keys(stats.studentAverages).length > 0 || Object.keys(stats.parentAverages).length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {Object.keys(stats.studentAverages).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Student Ratings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(stats.studentAverages).map(([key, avg]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{STUDENT_LABELS[key] || key}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${(avg / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-8 text-right">{avg}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {Object.keys(stats.parentAverages).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Parent Ratings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {Object.entries(stats.parentAverages).map(([key, avg]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{PARENT_LABELS[key] || key}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${(avg / 5) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-8 text-right">{avg}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={respondentType}
              onChange={(e) => { setRespondentType(e.target.value); handleFilterChange(); }}
              className="rounded-lg border border-border bg-card text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">All Types</option>
              <option value="student">Students</option>
              <option value="parent">Parents</option>
            </select>

            <select
              value={facebookFilter}
              onChange={(e) => { setFacebookFilter(e.target.value); handleFilterChange(); }}
              className="rounded-lg border border-border bg-card text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">Facebook Review: All</option>
              <option value="true">Willing to Review</option>
              <option value="false">Not Willing</option>
            </select>

            <select
              value={shareFilter}
              onChange={(e) => { setShareFilter(e.target.value); handleFilterChange(); }}
              className="rounded-lg border border-border bg-card text-foreground px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">Willing to Share: All</option>
              <option value="true">Willing to Share</option>
              <option value="false">Not Willing</option>
            </select>

            <Button variant="outline" onClick={() => { loadSurveys(); loadStats(); }}>
              <Filter className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Survey Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Responses ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingSpinner message="Loading surveys..." />
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadSurveys}>Retry</Button>
            </div>
          ) : surveys.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No survey responses found
            </p>
          ) : (
            <>
              <div className="table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Respondent</th>
                      <th>Type</th>
                      <th>Student</th>
                      <th>Avg Rating</th>
                      <th>Free Text</th>
                      <th>Facebook</th>
                      <th>Share</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surveys.map((survey) => (
                      <>
                        <tr
                          key={survey.surveyId}
                          className="cursor-pointer"
                          onClick={() => setExpandedRow(expandedRow === survey.surveyId ? null : survey.surveyId)}
                        >
                          <td>
                            <div>
                              <p className="font-medium">{survey.respondentName}</p>
                              <p className="text-sm text-muted-foreground">{survey.respondentEmail}</p>
                            </div>
                          </td>
                          <td>
                            <Badge variant={survey.respondentType === 'parent' ? 'default' : 'secondary'}>
                              {survey.respondentType}
                            </Badge>
                          </td>
                          <td className="text-sm">{survey.studentName}</td>
                          <td>
                            <span className="font-medium">{averageRating(survey.ratings)}</span>
                            <span className="text-muted-foreground">/5</span>
                          </td>
                          <td className="max-w-[200px]">
                            {survey.freeText ? (
                              <p className="text-sm text-muted-foreground truncate" title={survey.freeText}>
                                {survey.freeText}
                              </p>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </td>
                          <td>
                            {survey.respondentType === 'parent' ? (
                              survey.willingToReviewFacebook ? (
                                <Badge variant="success" className="gap-1">
                                  <Users className="w-3 h-3" /> Yes
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">No</span>
                              )
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </td>
                          <td>
                            {survey.respondentType === 'parent' ? (
                              survey.willingToShare ? (
                                <Badge variant="success" className="gap-1">
                                  <Share2 className="w-3 h-3" /> Yes
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">No</span>
                              )
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </td>
                          <td className="text-muted-foreground text-sm">
                            {survey.completedAt
                              ? new Date(survey.completedAt).toLocaleDateString()
                              : '-'}
                          </td>
                        </tr>
                        {expandedRow === survey.surveyId && (
                          <tr key={`${survey.surveyId}-detail`}>
                            <td colSpan={8} className="bg-muted/30 !py-4">
                              <div className="px-4 space-y-3">
                                <p className="text-sm font-medium text-foreground">Individual Ratings:</p>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                  {Object.entries(survey.ratings).map(([key, value]) => {
                                    const labels = survey.respondentType === 'parent' ? PARENT_LABELS : STUDENT_LABELS;
                                    return (
                                      <div key={key} className="flex flex-col">
                                        <span className="text-xs text-muted-foreground">{labels[key] || key}</span>
                                        <span className="text-sm font-medium">{value}/5</span>
                                      </div>
                                    );
                                  })}
                                </div>
                                {survey.freeText && (
                                  <div>
                                    <p className="text-sm font-medium text-foreground">Free Text:</p>
                                    <p className="text-sm text-muted-foreground mt-1">{survey.freeText}</p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
